export interface HeatPoint {
  time: string;
  value: number;
}

export interface TimelineNode {
  time: string;
  title: string;
  desc: string;
  type: "origin" | "ferment" | "peak" | "spread" | "response";
}

export interface SpreadChannel {
  name: string;
  value: number;
}

export interface HotEvent {
  id: string;
  title: string;
  summary: string;
  category: string;
  plat: string;
  heat: number;
  trend: "rise" | "fall" | "steady";
  trendRate: number;
  topicCount: number;
  repost: number;
  rank: number;
  startDate: string;
  peakTime: string;
  duration: string;
  tags: string[];
  cover: string;
  heatSeries: HeatPoint[];
  timeline: TimelineNode[];
  channels: SpreadChannel[];
  analysis: string[];
}

export const categoryList = [
  { key: "全部", desc: "全部在追事件" },
  { key: "商业争议", desc: "合约、分成、价格与外泄录音" },
  { key: "带货翻车", desc: "选品、宣称与售后舆情" },
  { key: "人设崩塌", desc: "履历、旧言与真实身份核验" },
  { key: "情感纠纷", desc: "关系破裂、直播对峙与聊天记录" },
  { key: "网红言论", desc: "直播失言、评论翻车与截图扩散" },
];

export const siteStats = [
  { label: "在追话题", value: "1,286", unit: "个", note: "近 7 日纳入监测" },
  { label: "今日新增爆料", value: "73", unit: "条", note: "含二次发酵" },
  { label: "平均发酵时长", value: "4.6", unit: "小时", note: "从首爆到进入榜单" },
  { label: "覆盖内容平台", value: "12", unit: "个", note: "热搜 / 短视频 / 社群" },
];

export const fermentStages = [
  {
    step: "01",
    name: "首爆",
    window: "0 - 1 小时",
    desc: "一条截图或短视频在私域社群先出现，热度基数低但情绪浓度最高，往往决定了后续能不能出圈。",
    signal: "原始信源 · 转发链短",
  },
  {
    step: "02",
    name: "发酵",
    window: "1 - 4 小时",
    desc: "搬运号开始二次加工，标题被改写、片段被截取，话题量快速抬升，此时真假信息开始混合。",
    signal: "搬运号介入 · 话题量抬升",
  },
  {
    step: "03",
    name: "上热搜",
    window: "4 - 12 小时",
    desc: "讨论量突破平台阈值后进入热搜榜，媒体报道跟进，热度曲线出现第一个陡峭上升段。",
    signal: "榜单入围 · 媒体跟进",
  },
  {
    step: "04",
    name: "扩散与回落",
    window: "12 - 72 小时",
    desc: "跨平台扩散达到顶峰，随后当事人回应或新话题分流，热度进入长尾回落，但二次发酵风险仍存。",
    signal: "跨平台扩散 · 长尾回落",
  },
];

export const readGuide = [
  {
    icon: "ri-fire-line",
    title: "看首爆信源，不看转发量",
    desc: "转发量可以买，首条原始信源的时间戳与账号历史很难伪造。追踪时先定位 T+0 的那一条。",
  },
  {
    icon: "ri-line-chart-line",
    title: "盯曲线的斜率，而不是峰值",
    desc: "斜率代表扩散速度。同样破百万，两小时做到和两天做到，是完全不同的传播结构。",
  },
  {
    icon: "ri-share-forward-line",
    title: "记录跨平台跳转节点",
    desc: "从社群到短视频、从短视频到热搜的每一次跨越，都是热度倍增的关键节点。",
  },
  {
    icon: "ri-history-line",
    title: "为二次发酵留出观察窗",
    desc: "当事人回应、旧料补充、关联账号发声，都可能在 24 小时后重新拉起一条曲线。",
  },
];

export const hotEvents: HotEvent[] = [
  {
    id: "mcn-split-2026",
    title: "头部主播与MCN解约风波",
    summary:
      "一段疑似内部会议录音在运营群流出，随后双方各自发布声明，围绕账号归属与分成比例展开拉锯，事件在两天内三次登上热搜。",
    category: "商业争议",
    plat: "社群录音",
    heat: 9864200,
    trend: "rise",
    trendRate: 42,
    topicCount: 186,
    repost: 214,
    rank: 1,
    startDate: "2026-09-08 21:40",
    peakTime: "2026-09-09 11:20",
    duration: "51 小时",
    tags: ["解约", "账号归属", "分成", "录音"],
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20data%20network%20visualization%20with%20glowing%20orange%20nodes%20spreading%20across%20deep%20charcoal%20background%2C%20editorial%20minimalist%20tech%20aesthetic%2C%20warm%20amber%20accents%2C%20soft%20grain%20texture%2C%20no%20text&width=900&height=600&seq=gualian-case-mcn&orientation=landscape",
    heatSeries: [
      { time: "08日 22:00", value: 6 },
      { time: "09日 02:00", value: 14 },
      { time: "09日 06:00", value: 31 },
      { time: "09日 10:00", value: 68 },
      { time: "09日 12:00", value: 92 },
      { time: "09日 18:00", value: 100 },
      { time: "10日 02:00", value: 86 },
      { time: "10日 12:00", value: 74 },
    ],
    timeline: [
      {
        time: "T+0h 21:40",
        title: "会议录音在运营群流出",
        desc: "一条 47 秒的录音片段在三个私域运营群同时出现，内容是分成比例争执。此时仅在从业者小范围传播。",
        type: "origin",
      },
      {
        time: "T+2h 23:50",
        title: "行业搬运号首次加工",
        desc: "账号把录音转成文字长图，配上「头部主播要单飞」的标题，单条转发在两小时内破万。",
        type: "ferment",
      },
      {
        time: "T+8h 05:30",
        title: "话题量突破第一阈值",
        desc: "多个娱乐号跟进，话题词从行业圈扩散到泛娱乐受众，讨论开始涉及历史合作纠纷。",
        type: "ferment",
      },
      {
        time: "T+13h 11:20",
        title: "冲上热搜第九位",
        desc: "媒体开始求证，双方先后在社交平台发布简短声明，热度曲线出现全周期最陡的一段。",
        type: "peak",
      },
      {
        time: "T+26h 次日 23:00",
        title: "跨平台二次扩散",
        desc: "短视频端出现大量二创解读，话题量与转发量同步抬升，形成第二轮高峰。",
        type: "spread",
      },
      {
        time: "T+51h 第三日",
        title: "双方补充律师函声明",
        desc: "事件进入程序阶段，热度转入长尾回落，但关联话题仍在持续冒出。",
        type: "response",
      },
    ],
    channels: [
      { name: "短视频平台", value: 34 },
      { name: "微博热搜", value: 28 },
      { name: "私域社群", value: 22 },
      { name: "媒体跟进", value: 16 },
    ],
    analysis: [
      "这条事件的传播结构是典型的「私域起爆、公域放大」。首发场景在三个封闭运营群，信源可信度被社群关系背书，因此第一波转发几乎没有质疑成本。",
      "真正让话题出圈的是 T+2h 的文字长图。搬运号把 47 秒音频转成了可截图的文字，这一步降低了传播门槛，也让后续所有讨论都建立在同一份二手材料上。",
      "值得注意的是，热度曲线在 T+13h 出现陡升后并没有立刻回落，而是维持了约 13 小时的高位。这说明媒体求证与双方声明形成了「回应即二次传播」的循环，是判断事件能否长尾的关键信号。",
    ],
  },
  {
    id: "beauty-claim",
    title: "美妆博主成分宣称争议",
    summary:
      "有用户贴出检测报告质疑某款联名护肤品的成分标注，博主直播间回应被指态度强硬，事件由测评区扩散至消费维权话题。",
    category: "带货翻车",
    plat: "测评视频",
    heat: 8231600,
    trend: "steady",
    trendRate: 8,
    topicCount: 142,
    repost: 168,
    rank: 2,
    startDate: "2026-09-07 14:10",
    peakTime: "2026-09-08 20:40",
    duration: "68 小时",
    tags: ["成分争议", "检测报告", "联名款", "直播回应"],
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20macro%20shot%20of%20translucent%20amber%20liquid%20droplets%20on%20warm%20beige%20surface%2C%20editorial%20product%20photography%2C%20soft%20studio%20light%2C%20coral%20red%20accents%2C%20clean%20minimal%20composition%2C%20no%20text&width=900&height=600&seq=gualian-case-beauty&orientation=landscape",
    heatSeries: [
      { time: "07日 14:00", value: 8 },
      { time: "07日 20:00", value: 22 },
      { time: "08日 02:00", value: 38 },
      { time: "08日 08:00", value: 56 },
      { time: "08日 14:00", value: 78 },
      { time: "08日 20:00", value: 96 },
      { time: "09日 08:00", value: 90 },
      { time: "10日 08:00", value: 88 },
    ],
    timeline: [
      {
        time: "T+0h 14:10",
        title: "测评区的对比视频出现",
        desc: "一位中小测评者发布成分对比，画面直接展示检测报告截图，播放量在首日约 12 万。",
        type: "origin",
      },
      {
        time: "T+6h 20:00",
        title: "评论区聚集质疑",
        desc: "购买用户开始在评论区晒单并追问，博主账号当晚删除部分评论，被截图后引发第二波讨论。",
        type: "ferment",
      },
      {
        time: "T+30h",
        title: "直播间正面回应",
        desc: "博主在直播间用约十二分钟回应，措辞强硬，回放片段被单独剪出并配字幕扩散。",
        type: "peak",
      },
      {
        time: "T+42h",
        title: "移入消费维权话题",
        desc: "有媒体把事件放入带货合规框架讨论，话题从个人争议上升为行业议题。",
        type: "spread",
      },
      {
        time: "T+68h",
        title: "品牌方发布检测说明",
        desc: "品牌方补充完整检测流程说明，热度开始回落，但联名款销量数据仍被反复引用。",
        type: "response",
      },
    ],
    channels: [
      { name: "短视频平台", value: 40 },
      { name: "电商评论区", value: 24 },
      { name: "微博热搜", value: 20 },
      { name: "媒体跟进", value: 16 },
    ],
    analysis: [
      "与录音类事件不同，这条内容的起爆点是「可视化证据」——检测报告截图比任何文字描述都更适合被转发。视频与图片的组合让质疑几乎不需要额外解释。",
      "删除评论是本次传播的关键转折。被删除的评论以截图形式流向其他平台，形成了「越删越传」的经典结构，热度在 T+30h 反而冲高。",
      "事件后期从个人争议升级为行业合规讨论，讨论对象不再只是博主本人，这让热度的回落变得非常缓慢，长尾明显拉长。",
    ],
  },
  {
    id: "persona-fall",
    title: "励志人设被扒学历存疑",
    summary:
      "某主打逆袭经历的博主，其公开自述中的院校与获奖经历被网友逐条核对，多个时间节点无法对应，话题在深夜集中爆发。",
    category: "人设崩塌",
    plat: "论坛整理帖",
    heat: 7621900,
    trend: "fall",
    trendRate: -12,
    topicCount: 128,
    repost: 139,
    rank: 3,
    startDate: "2026-09-06 23:20",
    peakTime: "2026-09-07 09:50",
    duration: "44 小时",
    tags: ["学历存疑", "时间线", "旧采访", "人设"],
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20layered%20paper%20documents%20with%20warm%20amber%20light%20streaks%20on%20muted%20cream%20background%2C%20editorial%20conceptual%20photography%2C%20soft%20shadows%2C%20minimal%20composition%2C%20no%20text&width=900&height=600&seq=gualian-case-persona&orientation=landscape",
    heatSeries: [
      { time: "06日 23:00", value: 5 },
      { time: "07日 01:00", value: 19 },
      { time: "07日 03:00", value: 46 },
      { time: "07日 06:00", value: 72 },
      { time: "07日 10:00", value: 98 },
      { time: "07日 16:00", value: 88 },
      { time: "08日 06:00", value: 62 },
      { time: "08日 20:00", value: 44 },
    ],
    timeline: [
      {
        time: "T+0h 23:20",
        title: "论坛出现逐条核对帖",
        desc: "一篇长帖把博主公开自述中的时间节点整理成表格，指出三处无法对应，当晚回复量破千。",
        type: "origin",
      },
      {
        time: "T+3h 02:30",
        title: "旧采访被翻出比对",
        desc: "网友找出两年前的访谈视频，与近期简介中的表述并列，形成对比图并在凌晨被大量转发。",
        type: "ferment",
      },
      {
        time: "T+10h 09:50",
        title: "深夜话题冲高后进入热搜",
        desc: "由于爆发时间在凌晨，讨论在上午集中释放，热度曲线呈现罕见的「后置峰值」。",
        type: "peak",
      },
      {
        time: "T+30h",
        title: "本人关闭评论区",
        desc: "账号关闭评论区并短暂停更，相关截图成为新一轮传播素材。",
        type: "spread",
      },
      {
        time: "T+44h",
        title: "热度进入回落",
        desc: "缺少新增证据，讨论逐渐转向同类人设话题，主体事件热度快速下降。",
        type: "response",
      },
    ],
    channels: [
      { name: "论坛贴吧", value: 32 },
      { name: "私域社群", value: 26 },
      { name: "短视频平台", value: 26 },
      { name: "微博热搜", value: 16 },
    ],
    analysis: [
      "这是一次典型的「考据型」传播。驱动力不是情绪而是证据整理，论坛长帖与对比图构成了完整的信息链条，转发时几乎不需要附加解释。",
      "爆发时段在深夜，导致热度曲线出现「后置峰值」——凌晨积累、上午释放。追踪时如果只看当天实时榜，很容易低估这类事件的实际规模。",
      "由于核心争议需要逐条核对，参与门槛较高，热度在缺少新证据后下降得快，属于典型的短周期、高浓度事件。",
    ],
  },
  {
    id: "couple-drama",
    title: "网红情侣分手直播对峙",
    summary:
      "两人在同一天先后开播回应分手原因，聊天记录与语音片段被双方轮番放出，直播在线人数创下近期新高。",
    category: "情感纠纷",
    plat: "直播连麦",
    heat: 6418800,
    trend: "rise",
    trendRate: 27,
    topicCount: 117,
    repost: 122,
    rank: 4,
    startDate: "2026-09-09 19:30",
    peakTime: "2026-09-09 22:10",
    duration: "29 小时",
    tags: ["直播对峙", "聊天记录", "语音", "连麦"],
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20overlapping%20glowing%20coral%20and%20amber%20light%20ribbons%20in%20dark%20space%2C%20editorial%20conceptual%20art%2C%20dramatic%20warm%20contrast%2C%20minimal%20composition%2C%20no%20text&width=900&height=600&seq=gualian-case-couple&orientation=landscape",
    heatSeries: [
      { time: "09日 19:30", value: 12 },
      { time: "09日 20:30", value: 34 },
      { time: "09日 21:20", value: 58 },
      { time: "09日 22:10", value: 94 },
      { time: "09日 23:30", value: 100 },
      { time: "10日 02:00", value: 72 },
      { time: "10日 08:00", value: 52 },
      { time: "10日 20:00", value: 38 },
    ],
    timeline: [
      {
        time: "T+0h 19:30",
        title: "一方突然开播",
        desc: "账号在未预告的情况下开启直播，开场即回应分手传闻，同时在线人数在十分钟内突破十万。",
        type: "origin",
      },
      {
        time: "T+1h 20:30",
        title: "另一方同步开播",
        desc: "另一位当事人在同一时段开播，双方隔空回应，观众在两条直播间之间来回切换。",
        type: "ferment",
      },
      {
        time: "T+2h40 22:10",
        title: "聊天记录被放出",
        desc: "截图与语音片段在高潮时被放出，话题量瞬间抬升，热度曲线出现单点尖峰。",
        type: "peak",
      },
      {
        time: "T+6h 01:30",
        title: "录屏二创铺开",
        desc: "大量录屏与剪辑在短视频端铺开，把直播内容转成了可持续观看的短片。",
        type: "spread",
      },
      {
        time: "T+29h",
        title: "双方先后停播",
        desc: "两条直播间先后关闭，讨论转向对直播式公关的行业讨论。",
        type: "response",
      },
    ],
    channels: [
      { name: "直播平台", value: 38 },
      { name: "短视频平台", value: 30 },
      { name: "微博热搜", value: 18 },
      { name: "私域社群", value: 14 },
    ],
    analysis: [
      "直播型事件的最大特征是「实时性」——所有内容在发生的那一刻就被数万人在线见证，不存在后期剪辑的空间，因此可信度感知很高。",
      "双方同步开播形成了罕见的「双信源同时段」结构，观众需要在两条直播间之间切换，反而让单位时间内的讨论密度成倍增加。",
      "录屏二创是本次长尾的主要来源。直播结束不等于传播结束，剪辑版本把 3 小时的直播压缩成几十秒的片段，延续了热度。",
    ],
  },
  {
    id: "livestream-price",
    title: "直播间价格协议录音外泄",
    summary:
      "一段涉及保价条款的沟通录音被公开，多个品牌方被指在不同直播间给出不同机制，事件迅速演变为对带货定价的行业讨论。",
    category: "商业争议",
    plat: "录音曝光",
    heat: 5382400,
    trend: "steady",
    trendRate: 5,
    topicCount: 96,
    repost: 88,
    rank: 5,
    startDate: "2026-09-05 16:20",
    peakTime: "2026-09-06 10:30",
    duration: "76 小时",
    tags: ["保价条款", "定价机制", "品牌方", "录音"],
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20waveform%20audio%20visualization%20in%20warm%20amber%20and%20deep%20charcoal%2C%20editorial%20data%20art%2C%20soft%20grain%2C%20minimal%20balanced%20composition%2C%20no%20text&width=900&height=600&seq=gualian-case-price&orientation=landscape",
    heatSeries: [
      { time: "05日 16:00", value: 7 },
      { time: "05日 22:00", value: 21 },
      { time: "06日 04:00", value: 40 },
      { time: "06日 10:00", value: 82 },
      { time: "06日 16:00", value: 94 },
      { time: "07日 10:00", value: 88 },
      { time: "08日 10:00", value: 84 },
      { time: "09日 10:00", value: 86 },
    ],
    timeline: [
      {
        time: "T+0h 16:20",
        title: "录音片段出现在行业社区",
        desc: "一段约两分钟的沟通录音被贴在从业者社区，涉及保价与机制差异。",
        type: "origin",
      },
      {
        time: "T+6h 22:00",
        title: "多家品牌被点名",
        desc: "网友根据录音中的称呼整理出品牌清单，讨论从录音本身转向定价公平性。",
        type: "ferment",
      },
      {
        time: "T+18h 10:30",
        title: "登上热搜后进入平台回应",
        desc: "平台方表示将核查机制，相关话题在同一时段进入热搜前十。",
        type: "peak",
      },
      {
        time: "T+40h",
        title: "行业媒体集中报道",
        desc: "多家行业媒体从渠道定价角度做深度解读，讨论转为长期议题。",
        type: "spread",
      },
      {
        time: "T+76h",
        title: "热度维持在中位",
        desc: "缺少新证据但议题价值高，热度曲线呈现少见的「平台型」长尾。",
        type: "response",
      },
    ],
    channels: [
      { name: "行业社区", value: 30 },
      { name: "媒体跟进", value: 26 },
      { name: "微博热搜", value: 24 },
      { name: "社群转发", value: 20 },
    ],
    analysis: [
      "这条事件的热度曲线非常特别：没有陡峭尖峰，而是长时间维持在中高位，属于罕见的「平台型」曲线。",
      "原因在于争议对象是一个机制而非一个人。机制类议题不会因为当事人沉默而消失，反而会持续被新的案例补充，因此长尾异常坚固。",
      "追踪这类事件时，转发量不是最有效的指标，媒体跟进的数量与行业社区的发帖密度更能反映真实热度。",
    ],
  },
  {
    id: "food-review",
    title: "探店博主差评遭商家反击",
    summary:
      "一条探店差评视频发布后，商家公开监控与下单记录进行反驳，双方各执一词，事件引发对探店内容真实性的讨论。",
    category: "网红言论",
    plat: "短视频差评",
    heat: 4126900,
    trend: "fall",
    trendRate: -18,
    topicCount: 74,
    repost: 63,
    rank: 6,
    startDate: "2026-09-04 12:00",
    peakTime: "2026-09-05 09:10",
    duration: "38 小时",
    tags: ["探店", "监控", "反转", "内容真实性"],
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20warm%20tabletop%20scene%20with%20amber%20lamp%20glow%20and%20soft%20steam%2C%20editorial%20food%20photography%20mood%2C%20muted%20cream%20backdrop%2C%20minimal%20composition%2C%20no%20text&width=900&height=600&seq=gualian-case-food&orientation=landscape",
    heatSeries: [
      { time: "04日 12:00", value: 9 },
      { time: "04日 18:00", value: 24 },
      { time: "05日 00:00", value: 42 },
      { time: "05日 09:00", value: 90 },
      { time: "05日 15:00", value: 70 },
      { time: "05日 23:00", value: 52 },
      { time: "06日 12:00", value: 34 },
      { time: "07日 00:00", value: 22 },
    ],
    timeline: [
      {
        time: "T+0h 12:00",
        title: "差评视频发布",
        desc: "博主发布探店视频，指出出餐与卫生问题，评论区迅速站队。",
        type: "origin",
      },
      {
        time: "T+6h 18:00",
        title: "商家发布反驳内容",
        desc: "商家放出监控与下单记录，称部分描述与事实不符，事件开始出现「反转」标签。",
        type: "ferment",
      },
      {
        time: "T+21h 09:10",
        title: "话题进入本地热搜",
        desc: "两地网友围绕消费体验展开争论，热度在上午达到全周期最高点。",
        type: "peak",
      },
      {
        time: "T+27h",
        title: "双方各自删除部分内容",
        desc: "双方先后隐藏了部分内容，被截图后形成新一轮讨论。",
        type: "spread",
      },
      {
        time: "T+38h",
        title: "热度快速回落",
        desc: "缺少后续证据，事件热度在两天内回落至基准线。",
        type: "response",
      },
    ],
    channels: [
      { name: "短视频平台", value: 42 },
      { name: "本地社群", value: 24 },
      { name: "微博热搜", value: 20 },
      { name: "点评平台", value: 14 },
    ],
    analysis: [
      "反转类事件的核心变量是「证据出现的时间」。商家在 T+6h 放出监控，恰好卡在第一波热度尚未见顶时，直接改变了舆论走向。",
      "由于争议高度本地化，热度主要集中在同城社群与本地话题榜，全国性平台的表现反而一般，这类事件容易被全国榜低估。",
      "双方删除内容后的截图传播，说明在高热度阶段任何删除动作本身都会成为新素材，是追踪时不可忽略的细节。",
    ],
  },
  {
    id: "charity-doubt",
    title: "公益带货善款去向质疑",
    summary:
      "一场标称公益性质的专场直播结束后，网友要求公开善款明细，主播团队发布阶段性说明，讨论集中在信息透明度上。",
    category: "商业争议",
    plat: "评论质疑",
    heat: 3861500,
    trend: "rise",
    trendRate: 16,
    topicCount: 68,
    repost: 57,
    rank: 7,
    startDate: "2026-09-03 20:10",
    peakTime: "2026-09-04 15:40",
    duration: "57 小时",
    tags: ["公益专场", "善款明细", "透明度", "说明"],
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20soft%20amber%20light%20through%20frosted%20glass%20panels%20on%20warm%20neutral%20background%2C%20editorial%20conceptual%20photography%2C%20gentle%20gradient%2C%20minimal%20composition%2C%20no%20text&width=900&height=600&seq=gualian-case-charity&orientation=landscape",
    heatSeries: [
      { time: "03日 20:00", value: 6 },
      { time: "04日 02:00", value: 16 },
      { time: "04日 08:00", value: 34 },
      { time: "04日 15:00", value: 78 },
      { time: "04日 22:00", value: 88 },
      { time: "05日 12:00", value: 72 },
      { time: "06日 08:00", value: 56 },
      { time: "06日 22:00", value: 48 },
    ],
    timeline: [
      {
        time: "T+0h 20:10",
        title: "评论区出现明细追问",
        desc: "直播结束后，多条评论追问善款使用进度，团队当时未作回应。",
        type: "origin",
      },
      {
        time: "T+6h 02:00",
        title: "整理帖出现",
        desc: "网友把历次公益专场的数据整理成表格，指出前后口径不一致。",
        type: "ferment",
      },
      {
        time: "T+19h 15:40",
        title: "团队发布阶段说明",
        desc: "团队公布阶段性进展与流程图，但未给出逐笔明细，讨论转向透明度标准。",
        type: "peak",
      },
      {
        time: "T+34h",
        title: "公益机构参与讨论",
        desc: "有从业者科普公益信息披露规范，把个人争议拓展为行业常识讨论。",
        type: "spread",
      },
      {
        time: "T+57h",
        title: "热度温和回落",
        desc: "事件缺少对抗性情节，热度回落较快，但相关科普内容仍在传播。",
        type: "response",
      },
    ],
    channels: [
      { name: "电商评论区", value: 30 },
      { name: "私域社群", value: 26 },
      { name: "微博热搜", value: 24 },
      { name: "媒体跟进", value: 20 },
    ],
    analysis: [
      "透明性议题天然适合长文与图表传播，因此这次事件的二次加工内容质量普遍较高，也是它讨论氛围相对理性的原因。",
      "团队回应给出了进展但未给逐笔明细，属于「部分满足」式回应，热度没有立刻下降，而是转入对披露标准的讨论。",
      "对比同一时期的事件可以发现，当争议缺少对抗性情节时，热度峰值更低、回落更快，情绪浓度是决定曲线形状的核心变量。",
    ],
  },
  {
    id: "viral-outburst",
    title: "深夜直播失控言论截屏扩散",
    summary:
      "一场深夜直播中的情绪化发言被截屏，次日以图片形式在多平台扩散，事件在短时间内形成大量二次解读。",
    category: "网红言论",
    plat: "直播截屏",
    heat: 3288400,
    trend: "fall",
    trendRate: -22,
    topicCount: 59,
    repost: 71,
    rank: 8,
    startDate: "2026-09-02 01:20",
    peakTime: "2026-09-02 10:00",
    duration: "26 小时",
    tags: ["截屏", "情绪发言", "二次解读", "深夜直播"],
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20glowing%20coral%20text%20fragments%20scattered%20in%20dark%20space%20with%20amber%20rim%20light%2C%20editorial%20conceptual%20art%2C%20soft%20grain%2C%20minimal%20composition%2C%20no%20text&width=900&height=600&seq=gualian-case-outburst&orientation=landscape",
    heatSeries: [
      { time: "02日 01:00", value: 8 },
      { time: "02日 03:00", value: 26 },
      { time: "02日 06:00", value: 48 },
      { time: "02日 10:00", value: 92 },
      { time: "02日 14:00", value: 68 },
      { time: "02日 20:00", value: 44 },
      { time: "03日 02:00", value: 26 },
      { time: "03日 12:00", value: 15 },
    ],
    timeline: [
      {
        time: "T+0h 01:20",
        title: "深夜直播出现情绪发言",
        desc: "直播在线人数不高，但片段被现场观众截屏并保存，成为事件的唯一原始素材。",
        type: "origin",
      },
      {
        time: "T+3h 04:00",
        title: "截屏进入多个社群",
        desc: "图片在夜间通过社群横向扩散，第一批讨论集中在断句与语境还原。",
        type: "ferment",
      },
      {
        time: "T+9h 10:00",
        title: "二次解读集中出现",
        desc: "大量账号配上自己的解读发布，同一张图被赋予不同含义，热度达到峰值。",
        type: "peak",
      },
      {
        time: "T+14h",
        title: "本人发布澄清",
        desc: "当事人说明语境，部分讨论转向「情绪是否能被理解」，热度迅速下降。",
        type: "spread",
      },
      {
        time: "T+26h",
        title: "事件基本结束",
        desc: "缺乏新增素材，热度回归基准，但截屏本身仍在被引用。",
        type: "response",
      },
    ],
    channels: [
      { name: "私域社群", value: 34 },
      { name: "短视频平台", value: 28 },
      { name: "微博热搜", value: 22 },
      { name: "论坛贴吧", value: 16 },
    ],
    analysis: [
      "图片型素材的传播效率极高，但信息量低，因此必然伴随大量「二次解读」，同一张截屏可以衍生出完全相反的结论。",
      "这条事件的峰值出现在发布后约 9 小时，属于「慢热型」——夜间积累、白天集中释放，与深夜爆发的考据类事件曲线高度相似。",
      "当事人澄清后热度快速下降，说明当争议主要来自语境缺失时，一次有效澄清的作用非常明显。",
    ],
  },
  {
    id: "idol-trainee",
    title: "选秀网红旧照与言论被翻出",
    summary:
      "一位刚走红的选秀出身网红，其早年社交平台言论与旧照被集中整理，团队回应称部分内容被断章取义。",
    category: "人设崩塌",
    plat: "整理长图",
    heat: 2741200,
    trend: "steady",
    trendRate: 3,
    topicCount: 47,
    repost: 42,
    rank: 9,
    startDate: "2026-09-01 16:00",
    peakTime: "2026-09-02 11:30",
    duration: "41 小时",
    tags: ["旧料考古", "断章取义", "选秀", "团队回应"],
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20faded%20photographic%20frames%20overlapping%20with%20amber%20glow%20on%20cream%20paper%20texture%2C%20editorial%20conceptual%20collage%2C%20nostalgic%20warm%20tones%2C%20minimal%20composition%2C%20no%20text&width=900&height=600&seq=gualian-case-idol&orientation=landscape",
    heatSeries: [
      { time: "01日 16:00", value: 5 },
      { time: "01日 22:00", value: 18 },
      { time: "02日 04:00", value: 36 },
      { time: "02日 11:30", value: 84 },
      { time: "02日 18:00", value: 80 },
      { time: "03日 06:00", value: 62 },
      { time: "03日 20:00", value: 50 },
      { time: "04日 08:00", value: 46 },
    ],
    timeline: [
      {
        time: "T+0h 16:00",
        title: "整理长图发布",
        desc: "网友把早年的公开言论整理成长图，标注时间与来源，首日转发集中在粉丝社群内部。",
        type: "origin",
      },
      {
        time: "T+6h 22:00",
        title: "旧照加入讨论",
        desc: "旧照被补充进讨论，话题从言论转向形象一致性，讨论范围扩大。",
        type: "ferment",
      },
      {
        time: "T+19h 11:30",
        title: "进入热搜前十",
        desc: "媒体求证后，团队回应称部分内容被断章取义，热度在此刻达到峰值。",
        type: "peak",
      },
      {
        time: "T+30h",
        title: "粉丝与网友围绕语境争论",
        desc: "双方围绕上下文展开还原争论，讨论形式以长文和截图对照为主。",
        type: "spread",
      },
      {
        time: "T+41h",
        title: "热度平稳回落",
        desc: "事件进入长尾，但相关长图仍在被引用，形成缓慢的持续曝光。",
        type: "response",
      },
    ],
    channels: [
      { name: "私域社群", value: 32 },
      { name: "论坛贴吧", value: 26 },
      { name: "微博热搜", value: 26 },
      { name: "短视频平台", value: 16 },
    ],
    analysis: [
      "考古型事件的素材全部来自公开历史内容，信源本身没有争议，因此讨论焦点会迅速转移到「语境是否完整」上。",
      "这类事件的传播主力往往是粉丝与反对者的对冲，双方都在生产长图与对照表，反而让话题量维持得更久。",
      "团队回应中「断章取义」这一表述，实际上把争议从事实层面推到了语境层面，热度因此没有崩落，而是转为平缓的长尾。",
    ],
  },
];

export const siteCategories = ["全部", "商业争议", "带货翻车", "人设崩塌", "情感纠纷", "网红言论"];